/**
 * Table des noeuds de ressource recoltables.
 *
 * `recolte` est le lot rendu au coup FINAL: les coups intermediaires ne rendent
 * rien (voir shared/sim/ressources.ts). Le bonus de minerai du rocher n'est pas
 * dans la table parce qu'il est aleatoire: il se decide dans `frapper`.
 * Le biome 'prairie' n'existe pas dans ce monde (cinq biomes seulement), donc
 * le buisson a baies vit dans la foret centrale.
 */
import type { RessourceDef, RessourceKind } from '../types/monde.ts'

export const RESSOURCES: Record<RessourceKind, RessourceDef> = {
  arbre: {
    kind: 'arbre',
    nom: 'Arbre',
    recolte: { bois: 3, feuille: 1 },
    coups: 3,
    action: 'couper',
    biomes: ['foret', 'montagne'],
  },
  palmier: {
    kind: 'palmier',
    nom: 'Palmier',
    recolte: { bois: 2, noix_coco: 1 },
    coups: 3,
    action: 'couper',
    biomes: ['plage'],
  },
  rocher: {
    kind: 'rocher',
    nom: 'Rocher',
    recolte: { pierre: 2 },
    coups: 4,
    action: 'miner',
    biomes: ['montagne', 'glacier'],
    /*
     * UN ROCHER REPOUSSE, MAIS TRES LENTEMENT.
     *
     * Tout repoussait en trois jours de jeu, la pierre comme les baies. Un
     * buisson qui refait ses fruits en trois jours est credible; un rocher
     * abattu qui se reforme aussi vite l'est beaucoup moins, et c'est ce qui
     * faisait de la montagne un distributeur. Dix jours (quatre heures
     * reelles), c'est assez long pour qu'on ne compte plus dessus, et assez
     * court pour qu'une montagne raclee redevienne une montagne.
     *
     * Ce n'est possible que DEPUIS QUE LA MINE EXISTE: elle est la source
     * reguliere de minerai, le rocher redevient une trouvaille.
     */
    repousseJours: 10,
  },
  buisson_baies: {
    kind: 'buisson_baies',
    nom: 'Buisson à baies',
    recolte: { baie: 3, fibre: 1 },
    coups: 1,
    action: 'cueillir',
    biomes: ['foret'],
  },
  cactus: {
    kind: 'cactus',
    nom: 'Cactus',
    recolte: { figue: 1, fibre: 2 },
    coups: 2,
    action: 'cueillir',
    biomes: ['desert'],
  },
  bloc_glace: {
    kind: 'bloc_glace',
    nom: 'Bloc de glace',
    recolte: { glace: 2 },
    coups: 3,
    action: 'miner',
    biomes: ['glacier'],
  },

  /*
   * LE CRISTAL — la seule espece que l'ile 2 ait apportee, et la raison est
   * mesurable: sans elle, le Canyon de Cristal serait indiscernable de la
   * montagne du monde 1, puisqu'on y ramasserait exactement les memes rochers.
   * Toutes les autres regions se contentent de ce qui existe (les palmiers de
   * la plage, les cactus du desert, les blocs de glace du glacier), et la ferme
   * ne recolte RIEN: ses cultures sont des batiments a table `recolte`.
   *
   * Il repousse lentement, comme le rocher: un cristal qui se reforme en trois
   * jours ferait du canyon un distributeur, et il y a une mine pour ca.
   */
  cristal: {
    kind: 'cristal',
    nom: 'Cristal',
    recolte: { pierre: 1, minerai: 1 },
    coups: 4,
    action: 'miner',
    biomes: ['cristal'],
    repousseJours: 10,
  },

  /*
   * L'ARBRE LILAS — la couleur du Lac Lilas et de la Foret Fabula.
   *
   * Il rend exactement ce que rend un arbre: c'est un ARBRE, d'une autre
   * essence. Inventer une matiere « fleur de lilas » aurait demande un objet,
   * une icone et une recette pour lui donner un emploi — et une matiere sans
   * emploi est un trophee, pas une ressource.
   */
  arbre_lilas: {
    kind: 'arbre_lilas',
    nom: 'Arbre lilas',
    recolte: { bois: 3, fibre: 1 },
    coups: 3,
    action: 'couper',
    biomes: ['foret'],
  },
}

export function ressourceDef(k: RessourceKind): RessourceDef {
  return RESSOURCES[k]
}
